// templateOps.ts — immutable helpers for editing an EditorLevel template. The editor holds one template
// in React state; every edit returns a NEW template (structural sharing where cheap) so React re-renders
// cleanly. Pure data ops — no rendering, no I/O.
import { GridDirection } from "../Models/CellData";
import { MarbleColor } from "../Models/LevelData";
import { CellModifierType } from "../Models/CellModifier";
import { CURRENT_SCHEMA_VERSION, LevelDifficulty, SeedStrategy } from "../Models/Editor/EditorLevel";
// The game board is a fixed BOARD_SIZE × BOARD_SIZE. New templates use it and it is not author-editable,
// but the bounds are read off the FILE — and they drive GridCanvas's empty-slot loop, which runs rows×cols
// however few cells the board holds. A file claiming 100000×100000 froze the tab on the first draw, so this
// is both the size the editor builds and the ceiling validateTemplate holds files to.
export const BOARD_SIZE = 7;
// The game ships exactly one lock shape: a LOCK_SIZE × LOCK_SIZE square. The author picks where it sits,
// never how big it is.
export const LOCK_SIZE = 3;
// A fresh lock unlocks after this many customers (slices) have been cleared; the author tunes it after.
export const DEFAULT_LOCK_COUNTER = 3;
// The most boxes one tunnel may spawn. Authoring headroom, not a design rule: the reference game's 840
// levels top out at 6 on a plain tunnel (8 across every container type it ships) against a default of 3
// here, so nothing real comes near this. What it exists for is that the count drives BOTH an array of
// per-spawn records and one inspector row each, with nothing bounding either — a hand-typed 200000, or the
// same number in a file off Drive, froze the tab on the next render with no way back to fix it.
export const MAX_TUNNEL_SPAWNS = 10;
// A split box's colour columns, left to right (C# CellData.SplitColumns; the game's MarbleBox.Split).
export const SPLIT_COLUMNS = 3;
// A spawn count as this editor will hold it. Applied wherever a count is set OR an array is sized, so a
// number too big to render cannot enter through an op — a file that already carries one is reported by
// validateTemplate instead (Save and Publish close), which is the only place that can still explain it.
function clampSpawnCount(count) {
    return Math.max(0, Math.min(MAX_TUNNEL_SPAWNS, Math.floor(count) || 0));
}
// The directionless spawners' fields, all off — what every conversion AWAY from a super tunnel, a tool box or
// a pipe writes, so a cell never carries a stale queue for a spawner it no longer is. The propeller flag rides
// along: it is the other standing item every conversion has to turn off.
const NO_SPAWNER = {
    isPropeller: false,
    propellerHasBox: false,
    isSuperTunnel: false,
    superTunnelSpawnCount: 0,
    superTunnelSpawns: [],
    isToolBox: false,
    toolBoxSpawnCount: 0,
    toolBoxSpawns: [],
    isPipe: false,
    pipeSpawnCount: 0,
    pipeSpawns: [],
};
// A fresh box cell at (x, y) with all feature flags off and the authoring trio at defaults.
export function makeCell(x, y) {
    return {
        x,
        y,
        isMystery: false,
        isArrow: false,
        arrowDirection: GridDirection.Bottom,
        isTunnel: false,
        tunnelDirection: GridDirection.Bottom,
        tunnelSpawnCount: 0,
        tunnelSpawns: [],
        ...NO_SPAWNER,
        isGate: false,
        gateLength: 0,
        gateMultiplier: 1,
        colorOverride: MarbleColor.None,
        sortOrder: 0,
        group: 0,
        sibling: 0,
    };
}
export function defaultGeneration() {
    return {
        paletteSize: 6,
        seed: 12345,
        strategy: SeedStrategy.FixedSeed,
        randColorList: [],
        evenDistribution: true,
        excludeSwaps: false,
        mysterySliceChances: [],
    };
}
// A new empty template of the given size (no cells).
export function createEmptyTemplate(columns, rows, name = "untitled") {
    return {
        schemaVersion: CURRENT_SCHEMA_VERSION,
        name,
        columns,
        rows,
        difficulty: LevelDifficulty.Normal,
        generation: defaultGeneration(),
        cells: { items: [], modifiers: [] },
    };
}
// `items` arrives off Drive with no schema check, so a hand-edited file can hold a null ENTRY — which the
// backend binds and skips without complaint (EditorCell is a class there), so such a file validates, saves
// and publishes. Nearly every op reaches the board through here, so one guard covers them all.
export function findCell(t, x, y) {
    return t.cells.items.find((c) => c != null && c.x === x && c.y === y);
}
export function hasCell(t, x, y) {
    return findCell(t, x, y) !== undefined;
}
// Adds a plain box at (x, y) if the cell is empty; no-op if one already exists.
export function withCellAdded(t, x, y) {
    if (hasCell(t, x, y)) {
        return t;
    }
    return { ...t, cells: { ...t.cells, items: [...t.cells.items, makeCell(x, y)] } };
}
// Removes the cell at (x, y) (and any modifiers that reference it) if present.
export function withCellRemoved(t, x, y) {
    if (!hasCell(t, x, y)) {
        return t;
    }
    return {
        ...t,
        cells: {
            items: t.cells.items.filter((c) => !(c.x === x && c.y === y)),
            // `cells` arrives off Drive with no schema check, so a hand-edited modifier can be missing it
            // entirely — reachable from every cell deletion. A modifier that can't be checked is left in
            // place rather than thrown on: it can't be proven to reference (x, y), so treat it as harmless.
            modifiers: t.cells.modifiers.filter((m) => !Array.isArray(m.cells) || !m.cells.some((p) => p.x === x && p.y === y)),
        },
    };
}
// Content equality for a tunnel's per-spawn array — compared by value, not identity, because every caller
// that patches `tunnelSpawns` (withCellAsBox, withCellAsTunnel, withTunnelSpawnCount) builds a fresh array
// literal even when the spawns themselves didn't change.
//
// The `?? []` is defence in depth, not the fix: normalizeTemplate materialises this array at the door, so a
// cell reaching here without one should be impossible. It is kept because this is the ONE read of the field
// that isn't already optional-safe (resizeSpawns, cloneCellAt and templateStats all are), and because the
// field is genuinely absent on every template written before it existed (EditorLevel.cs) — a crash here
// lands inside a React event handler, where it shows up as a shortcut that silently does nothing.
function sameTunnelSpawns(a, b) {
    const x = a ?? [];
    const y = b ?? [];
    // Every field a spawn carries has to be here, colour included. Comparing a SUBSET of them makes
    // withCellPatched call a real change a no-op and return the template untouched — which is what painting
    // a spawn's colour did until the colour was added to this list: the click did nothing, silently.
    return x.length === y.length && x.every((s, i) => s.group === y[i].group
        && s.sibling === y[i].sibling
        && s.sortOrder === y[i].sortOrder
        && (s.colorOverride ?? MarbleColor.None) === (y[i].colorOverride ?? MarbleColor.None));
}
// A split box's painted columns as the compiler reads them: entry k, or None when the entry is missing (or
// the key is not an array at all — it comes off a file unchecked; validateTemplate reports that one).
export function splitColumnPaint(cell, column) {
    const painted = cell.splitColorOverrides;
    return Array.isArray(painted) ? painted[column] ?? MarbleColor.None : MarbleColor.None;
}
// Two painted-column lists that paint the same thing. Null, an empty list and a list of Nones all paint
// nothing, so turning one into another must not count as an edit (nor burn an undo entry).
function sameSplitColumns(a, b) {
    const n = Math.max(Array.isArray(a) ? a.length : 0, Array.isArray(b) ? b.length : 0);
    for (let k = 0; k < n; k++) {
        const x = Array.isArray(a) ? a[k] ?? MarbleColor.None : MarbleColor.None;
        const y = Array.isArray(b) ? b[k] ?? MarbleColor.None : MarbleColor.None;
        if (x !== y) {
            return false;
        }
    }
    return true;
}
// True when every field named in `patch` already holds that value on `cell` — i.e. applying the patch
// would change nothing. `tunnelSpawns` gets content comparison (see sameTunnelSpawns); every other field is
// a primitive, so `===` is exact.
// `isCleared` is optional — absent on every template written before cleared cells existed — so an absent
// key has to read as `false`, or every conversion that clears it would look like a change to an old cell.
function patchIsNoOp(cell, patch) {
    return Object.keys(patch).every((k) => k === "tunnelSpawns"
        ? sameTunnelSpawns(cell.tunnelSpawns, patch.tunnelSpawns)
        : k === "superTunnelSpawns" || k === "toolBoxSpawns" || k === "pipeSpawns"
            ? sameTunnelSpawns(cell[k] ?? [], patch[k] ?? [])
            // The spawners' keys are optional (absent on every template written before them), and an absent
            // key means false / 0 — so "set it to false" on such a cell changes nothing. isCleared, isGlide,
            // isSplit and isDouble likewise.
            : k === "isSuperTunnel" || k === "isToolBox" || k === "isPipe" || k === "isPropeller" || k === "propellerHasBox" || k === "isCleared" || k === "isGlide" || k === "isSplit" || k === "isDouble" || k === "isJumpingJack" || k === "isDudBox"
                ? (cell[k] ?? false) === (patch[k] ?? false)
                // Absent on a propeller pointing Top (the C# writes it only when set), so absent reads as Top.
                : k === "propellerDirection"
                    ? (cell.propellerDirection ?? GridDirection.Top) === (patch.propellerDirection ?? GridDirection.Top)
                    : k === "innerColorOverride"
                        ? (cell.innerColorOverride ?? MarbleColor.None) === (patch.innerColorOverride ?? MarbleColor.None)
                        : k === "splitColorOverrides"
                            ? sameSplitColumns(cell.splitColorOverrides, patch.splitColorOverrides)
                            : k === "superTunnelSpawnCount" || k === "toolBoxSpawnCount" || k === "pipeSpawnCount"
                                ? (cell[k] ?? 0) === patch[k]
                                : cell[k] === patch[k]);
}
// A cell that holds no box of its own: a gate, a tunnel, a super tunnel, a tool box, a pipe, a propeller — or a
// CLEARED cell, which is bare ground with nothing on it. Everything a box flag, a paint, a connector or a seal applies to
// is the complement of this. (A tool box does leave once it has thrown its load, but until then it is a
// crate on the board, not a box the player can drop.)
export function isWall(c) {
    return c.isGate || c.isTunnel || (c.isSuperTunnel ?? false) || (c.isToolBox ?? false) || (c.isPipe ?? false) || (c.isPropeller ?? false) || (c.isCleared ?? false);
}
// Patches the cell at (x, y) with the given partial. No-op (SAME reference back) if the cell is empty, or
// if the patch wouldn't actually change any field — the latter matters because a keyboard shortcut can fire
// again with no visible effect (the same digit/mechanic pressed twice on a cell that already matches), and
// without this guard that would still push a fresh entry onto the undo stack.
export function withCellPatched(t, x, y, patch) {
    const cell = findCell(t, x, y);
    if (!cell || patchIsNoOp(cell, patch)) {
        return t;
    }
    return {
        ...t,
        cells: {
            ...t.cells,
            items: t.cells.items.map((c) => (c.x === x && c.y === y ? { ...c, ...patch } : c)),
        },
    };
}
// Converts/adds the cell at (x, y) to a plain box (clears tunnel/gate/mystery flags; keeps group/etc if
// the cell already existed). Deliberately does NOT touch `isArrow` — other callers (withBoxFlag) build on
// top of this to set one box flag while preserving the other, and need `isArrow` left alone to do that. Use
// `withCellAsPlainBox` below when the intent is "clear every box flag" (⇧1 / Box).
export function withCellAsBox(t, x, y) {
    if (!hasCell(t, x, y)) {
        return withCellAdded(t, x, y);
    }
    return withCellPatched(t, x, y, {
        isTunnel: false,
        isGate: false,
        isCleared: false,
        isMystery: false,
        gateLength: 0,
        gateMultiplier: 1,
        tunnelSpawnCount: 0,
        tunnelSpawns: [],
        ...NO_SPAWNER,
    });
}
// Converts/adds the cell at (x, y) to a plain box with `isMystery`, `isArrow`, `isGlide`, `isSplit` and `isDouble`
// off — this is what ⇧1 (Box) means: unlike withCellAsBox (which leaves isArrow alone for withBoxFlag's sake), Box clears
// every box flag and turns a tunnel/gate cell back into a plain box. Same-reference no-op when the cell is
// already exactly a plain box, so pressing ⇧1 again on one doesn't burn an undo entry.
export function withCellAsPlainBox(t, x, y) {
    const existing = findCell(t, x, y);
    if (existing && !isWall(existing) && !existing.isMystery && !existing.isArrow && !existing.isGlide && !existing.isSplit && !existing.isDouble && !existing.isJumpingJack && !existing.isDudBox) {
        return t;
    }
    const plain = withCellPatched(withCellAsBox(t, x, y), x, y, { isArrow: false, isGlide: false, isJumpingJack: false, isDudBox: false });
    return withDouble(withSplit(plain, x, y, false), x, y, false);
}
// The patch that turns the split flag on (or off) for `cell`, or null when there is nothing to do (it is not
// a box, or the flag already says so). The paint goes along with it, because the two flags are painted
// differently: a plain box's ONE colour becomes the split box's middle column (the column the game reads as
// the box's colour), and back again. The other two columns' paint is dropped when the box stops being split
// — there is no plain-box field to hold it — so undo is how to get it back.
export function splitPatch(cell, on) {
    if (isWall(cell) || (cell.isSplit ?? false) === on) {
        return null;
    }
    if (on) {
        const painted = cell.colorOverride ?? MarbleColor.None;
        return {
            isSplit: true,
            colorOverride: MarbleColor.None,
            splitColorOverrides: painted !== MarbleColor.None ? [MarbleColor.None, painted, MarbleColor.None] : null,
            // A double box is a plain box holding another: it cannot be split as well (see doublePatch).
            ...(cell.isDouble ? { isDouble: false, innerColorOverride: MarbleColor.None } : {}),
            ...(cell.isJumpingJack ? { isJumpingJack: false } : {}),
            ...(cell.isDudBox ? { isDudBox: false } : {}),
        };
    }
    const middle = splitColumnPaint(cell, 1);
    return {
        isSplit: false,
        colorOverride: middle !== MarbleColor.None ? middle : cell.colorOverride ?? MarbleColor.None,
        splitColorOverrides: null,
    };
}
// Turns the box at (x, y) into a split box, or back into a plain one (see splitPatch). Same reference back
// when nothing changes: an empty cell, a wall, or the flag already set that way.
export function withSplit(t, x, y, on) {
    const cell = findCell(t, x, y);
    const patch = cell ? splitPatch(cell, on) : null;
    return patch ? withCellPatched(t, x, y, patch) : t;
}
// The Split Box tool: places a split box on an empty cell, turns a wall into one, and splits a box that is
// already there — keeping its other box flags (mystery, arrow, glide), the way ⇧2 and ⇧3 keep each other's.
export function withCellAsSplitBox(t, x, y) {
    const existing = findCell(t, x, y);
    const box = existing && !isWall(existing) ? t : withCellAsBox(t, x, y);
    return withSplit(box, x, y, true);
}
// Converts/adds the cell at (x, y) to a box with `flag` set, PRESERVING whatever the other box flag
// (isMystery/isArrow) currently holds — this is what ⇧2 (Mystery) and ⇧3 (Arrow) mean: they are independent
// flags on a box, so ⇧2 then ⇧3 (or the reverse) must yield a mystery arrow, not one flag wiping the other.
// withCellAsBox alone can't be reused here as-is: it unconditionally clears isMystery, so the flag being
// preserved has to be read off the cell BEFORE conversion and re-applied after. Same-reference no-op when
// the cell is already a box with `flag` set (the other flag is always carried through unchanged, so it
// can't be the thing that differs).
export function withBoxFlag(t, x, y, flag) {
    const other = flag === "isMystery" ? "isArrow" : "isMystery";
    const existing = findCell(t, x, y);
    if (existing && !isWall(existing) && existing[flag]) {
        return t;
    }
    const otherValue = existing ? existing[other] : false;
    // A double box takes no other box flag (see doublePatch), so turning one on makes it an ordinary box again.
    const leaveDouble = existing?.isDouble ? { isDouble: false, innerColorOverride: MarbleColor.None } : {};
    const leaveJack = flag === "isArrow" && existing?.isJumpingJack ? { isJumpingJack: false } : {};
    return withCellPatched(withCellAsBox(t, x, y), x, y, { [flag]: true, [other]: otherValue, ...leaveDouble, ...leaveJack });
}
// The patch that turns the double flag on (or off) for `cell`, or null when there is nothing to do (it is not
// a box, or the flag already says so). A double box is a PLAIN box holding another — the game builds it
// without mystery, arrow, glide or split — so turning it on takes those off (a split box's middle column
// paint becomes the double box's colour, as un-splitting always does). Turning it off drops the inside paint:
// there is no plain-box field to hold it, so undo is how to get it back.
export function doublePatch(cell, on) {
    if (isWall(cell) || (cell.isDouble ?? false) === on) {
        return null;
    }
    if (on) {
        return {
            ...(cell.isSplit ? splitPatch(cell, false) : {}),
            isDouble: true,
            isMystery: false,
            isArrow: false,
            isGlide: false,
            isJumpingJack: false,
            isDudBox: false,
        };
    }
    return { isDouble: false, innerColorOverride: MarbleColor.None };
}
// Turns the box at (x, y) into a double box, or back into a plain one (see doublePatch). Same reference back
// when nothing changes: an empty cell, a wall, or the flag already set that way.
export function withDouble(t, x, y, on) {
    const cell = findCell(t, x, y);
    const patch = cell ? doublePatch(cell, on) : null;
    return patch ? withCellPatched(t, x, y, patch) : t;
}
export function jackPatch(cell, on) {
    if (isWall(cell) || (cell.isJumpingJack ?? false) === on) {
        return null;
    }
    if (on) {
        return {
            ...(cell.isSplit ? splitPatch(cell, false) : {}),
            ...(cell.isDouble ? { isDouble: false, innerColorOverride: MarbleColor.None } : {}),
            isJumpingJack: true,
            isArrow: false,
            isGlide: false,
            isDudBox: false,
        };
    }
    return { isJumpingJack: false };
}
export function withJumpingJack(t, x, y, on) {
    const cell = findCell(t, x, y);
    const patch = cell ? jackPatch(cell, on) : null;
    return patch ? withCellPatched(t, x, y, patch) : t;
}
export function withCellAsJumpingJack(t, x, y) {
    const existing = findCell(t, x, y);
    const box = existing && !isWall(existing) ? t : withCellAsBox(t, x, y);
    return withJumpingJack(box, x, y, true);
}
export function dudBoxPatch(cell, on) {
    if (isWall(cell) || (cell.isDudBox ?? false) === on) {
        return null;
    }
    if (on) {
        return {
            ...(cell.isSplit ? splitPatch(cell, false) : {}),
            ...(cell.isDouble ? { isDouble: false, innerColorOverride: MarbleColor.None } : {}),
            ...(cell.isJumpingJack ? { isJumpingJack: false } : {}),
            isDudBox: true,
        };
    }
    return { isDudBox: false };
}
export function withDudBox(t, x, y, on) {
    const cell = findCell(t, x, y);
    const patch = cell ? dudBoxPatch(cell, on) : null;
    return patch ? withCellPatched(t, x, y, patch) : t;
}
export function withCellAsDudBox(t, x, y) {
    const existing = findCell(t, x, y);
    const box = existing && !isWall(existing) ? t : withCellAsBox(t, x, y);
    return withDudBox(box, x, y, true);
}
// The Double Box tool: places a double box on an empty cell, turns a wall into one, and makes a box that is
// already there a double box (keeping its painted colour and its trio).
export function withCellAsDoubleBox(t, x, y) {
    const existing = findCell(t, x, y);
    const box = existing && !isWall(existing) ? t : withCellAsBox(t, x, y);
    return withDouble(box, x, y, true);
}
// Resizes a per-spawn record array to `count`, keeping existing entries and padding new ones with a copy of
// `fallback` (the cell's own trio), so each added spawn defaults to the tunnel's group/sibling/sortOrder.
function resizeSpawns(prev, count, fallback) {
    // A backstop, not the guard: every caller either clamps the count itself or refuses to touch a tunnel
    // whose count is over the ceiling (overCeiling below), so this can only fire if a future caller forgets.
    // It must never be the thing that DECIDES the length — clamping here while the cell's count says 50
    // wrote back a ten-entry array and silently destroyed the other forty authored records.
    return Array.from({ length: clampSpawnCount(count) }, (_, i) => (prev && i < prev.length ? { ...prev[i] } : { ...fallback }));
}
// A tunnel carrying more spawns than the editor can author. Such a template is an error state — the
// validator holds Save and Publish shut on it — so the ops leave it exactly as it arrived rather than
// half-rewriting it. The one exception is withTunnelSpawnCount: setting the count IS the author asking for
// a shorter list.
function overCeiling(cell) {
    return cell.tunnelSpawnCount > MAX_TUNNEL_SPAWNS;
}
// The cell's own authoring trio — the fallback for spawns without an explicit record. The COLOUR is
// deliberately not inherited: a colour painted on the tunnel cell means nothing (its boxes are its spawns,
// and the validator says so), so a spawn without a record starts on the seed. Mirrors
// LevelCompiler.TunnelSpawnFor, which builds the same fallback and leaves ColorOverride at None.
function cellTrio(cell) {
    return { group: cell.group, sibling: cell.sibling, sortOrder: cell.sortOrder, colorOverride: MarbleColor.None };
}
// A number as the C# binder would have it: anything that isn't finite (a missing key, a string, a null) is
// the int default, 0. Only for READING a file's trios — the ops never write a non-number.
function asInt(v) {
    return Number.isFinite(v) ? v : 0;
}
// The authoring trio of a tunnel's `index`-th spawned box, read exactly the way LevelCompiler.TunnelSpawnFor
// reads it: an index with no record of its own falls back to the tunnel cell's trio.
//
// Everything that SHOWS a spawn goes through here — the grid badges and the inspector rows — so what the
// editor draws for spawn k is the trio the compiler will colour spawn k with. Reading it per site is what let
// the two disagree: the tunnel cell's own `group` is a fallback, never what its boxes carry.
//
// asInt applies to BOTH paths, and it has to: normalizeTemplate repairs a null spawn RECORD but never looks
// inside a trio, so a file can carry a group that is missing, a string, an object. The record path having the
// guard and the fallback path not meant the same function promised a number down one branch and whatever the
// file held down the other — and the fallback branch is the one a tunnel with no records at all takes, i.e.
// every pip on it. What got drawn there was the text "undefined" in `hsl(NaN 70% 55%)`, which canvas ignores
// as a colour, leaving the pip filled with whatever was painted before it.
export function tunnelSpawnAt(cell, index) {
    const spawns = cell.tunnelSpawns;
    const rec = (Array.isArray(spawns) && index >= 0 && index < spawns.length ? spawns[index] : null) ?? cellTrio(cell);
    return {
        group: asInt(rec.group),
        sibling: asInt(rec.sibling),
        sortOrder: asInt(rec.sortOrder),
        colorOverride: rec.colorOverride ?? MarbleColor.None,
    };
}
// Converts/adds the cell at (x, y) to a tunnel (default direction Bottom, spawn count 3 if not already one).
// Clears `isArrow` along with the other box-only flags: Arrow is a box flag, and a tunnel that secretly
// still carried it would let a later withBoxFlag (which preserves "whatever the other flag currently holds"
// across a box<->tunnel round trip) read that stale value back as a real arrow the author never asked for.
export function withCellAsTunnel(t, x, y) {
    const existing = findCell(t, x, y);
    const base = withCellAdded(t, x, y);
    // A tunnel already over the ceiling keeps both its count and its records: clamping the count here would
    // shorten the array with it, throwing away authored records the author never asked to lose. The count is
    // an Error in the panel, and the Spawn count box is where it gets brought back into range.
    const keepSpawns = existing !== undefined && existing.isTunnel && overCeiling(existing);
    const count = existing && existing.isTunnel && existing.tunnelSpawnCount > 0 ? clampSpawnCount(existing.tunnelSpawnCount) : 3;
    const fallback = existing ? cellTrio(existing) : cellTrio(makeCell(x, y));
    return withCellPatched(base, x, y, {
        isTunnel: true,
        isGate: false,
        isCleared: false,
        isMystery: false,
        isArrow: false,
        isGlide: false,
        isSplit: false,
        splitColorOverrides: null,
        isDouble: false,
        innerColorOverride: MarbleColor.None,
        isJumpingJack: false,
        isDudBox: false,
        // Cleared for the same reason as isArrow above: a painted colour is a BOX field, and a tunnel that
        // secretly still carried one showed a warning in Issues the inspector had no control to clear (the
        // Colour row renders only for a plain box) — and ⇧1 back to a box read that stale value in again.
        colorOverride: MarbleColor.None,
        gateLength: 0,
        gateMultiplier: 1,
        ...NO_SPAWNER,
        tunnelSpawnCount: keepSpawns ? existing.tunnelSpawnCount : count,
        tunnelSpawns: keepSpawns
            ? existing.tunnelSpawns
            : resizeSpawns(existing && existing.isTunnel ? existing.tunnelSpawns : undefined, count, fallback),
        tunnelDirection: existing && existing.isTunnel ? existing.tunnelDirection : GridDirection.Bottom,
    });
}
// Converts/adds the cell at (x, y) to a CLEARED cell: board ground with nothing on it, as if a box had stood
// there and dropped, socket and all. The game draws no wall over it and builds nothing on it, and the reveal
// conducts through it from the start. It is not a box, so every box/tunnel/gate field goes back to its
// default — a stale one would only resurface on a later conversion back (the same reasoning as isArrow in
// withCellAsTunnel). The authoring trio is left alone: the compiler ignores it here, and keeping it means a
// cleared cell turned back into a box keeps the group it had. Same-reference no-op on a cleared cell.
export function withCellAsCleared(t, x, y) {
    const existing = findCell(t, x, y);
    if (existing && existing.isCleared) {
        return t;
    }
    return withCellPatched(withCellAdded(t, x, y), x, y, {
        isCleared: true,
        isMystery: false,
        isArrow: false,
        isGlide: false,
        isSplit: false,
        splitColorOverrides: null,
        isDouble: false,
        innerColorOverride: MarbleColor.None,
        isJumpingJack: false,
        isDudBox: false,
        isTunnel: false,
        tunnelSpawnCount: 0,
        tunnelSpawns: [],
        isGate: false,
        gateLength: 0,
        gateMultiplier: 1,
        colorOverride: MarbleColor.None,
        ...NO_SPAWNER,
    });
}
// The mechanics a paint stroke can lay down: one cell each, no modifiers, nothing multi-cell (a stroke of
// gates or locks would pile overlapping panels on top of each other). The page's `Mechanic` union is built
// ON this one, so the palette and `withMechanicPainted` cannot drift apart.
export const PAINTABLE_MECHANICS = ["box", "mystery", "arrow", "split", "double", "jumpingJack", "dudBox", "tunnel", "superTunnel", "toolBox", "pipe", "propeller", "cleared"];
// One cell of a paint stroke: places `mechanic` at (x, y) if that cell is empty. Returns the SAME template
// when the cell is already occupied (painting never overwrites what is there) or when the underlying op
// changes nothing — so the caller can tell "this stroke painted something" from template identity alone,
// with no separate flag to keep in step.
//
// Each branch is the very op applyMechanic uses for that mechanic: a painted cell and a clicked cell must be
// identical, or a stroke would quietly author something the palette never promised.
export function withMechanicPainted(t, x, y, mechanic) {
    if (hasCell(t, x, y)) {
        return t;
    }
    switch (mechanic) {
        case "tunnel":
            return withCellAsTunnel(t, x, y);
        case "superTunnel":
            return withCellAsSuperTunnel(t, x, y);
        case "toolBox":
            return withCellAsToolBox(t, x, y);
        case "pipe":
            return withCellAsPipe(t, x, y);
        case "propeller":
            return withCellAsPropeller(t, x, y);
        case "mystery":
            return withBoxFlag(t, x, y, "isMystery");
        case "arrow":
            return withBoxFlag(t, x, y, "isArrow");
        case "split":
            return withCellAsSplitBox(t, x, y);
        case "double":
            return withCellAsDoubleBox(t, x, y);
        case "jumpingJack":
            return withCellAsJumpingJack(t, x, y);
        case "dudBox":
            return withCellAsDudBox(t, x, y);
        case "box":
            return withCellAsPlainBox(t, x, y);
        case "cleared":
            return withCellAsCleared(t, x, y);
    }
}
// Sets a tunnel's spawn count and resizes its per-spawn record array to match (new spawns default to the
// cell's trio), clamped to MAX_TUNNEL_SPAWNS. No-op if the cell is missing.
export function withTunnelSpawnCount(t, x, y, count) {
    const cell = findCell(t, x, y);
    if (!cell) {
        return t;
    }
    const n = clampSpawnCount(count);
    return withCellPatched(t, x, y, { tunnelSpawnCount: n, tunnelSpawns: resizeSpawns(cell.tunnelSpawns, n, cellTrio(cell)) });
}
// Patches the index-th spawn's authoring trio (group / sibling / sortOrder).
export function withTunnelSpawn(t, x, y, index, patch) {
    const cell = findCell(t, x, y);
    // A tunnel over the ceiling is left untouched: writing one spawn means re-sizing the array, and the only
    // length this op could write is a clamped one — which would destroy every record past the ceiling to
    // change a single group. The inspector shows the first MAX_TUNNEL_SPAWNS rows of such a tunnel and the
    // ⛔ beside them says why they do nothing until the count is brought back into range.
    if (!cell || overCeiling(cell)) {
        return t;
    }
    const spawns = resizeSpawns(cell.tunnelSpawns, cell.tunnelSpawnCount, cellTrio(cell));
    if (index >= 0 && index < spawns.length) {
        spawns[index] = { ...spawns[index], ...patch };
    }
    return withCellPatched(t, x, y, { tunnelSpawns: spawns });
}
// The fields each spawner keeps its load in.
const SPAWNER_KEYS = {
    superTunnel: { flag: "isSuperTunnel", count: "superTunnelSpawnCount", spawns: "superTunnelSpawns" },
    toolBox: { flag: "isToolBox", count: "toolBoxSpawnCount", spawns: "toolBoxSpawns" },
    pipe: { flag: "isPipe", count: "pipeSpawnCount", spawns: "pipeSpawns" },
};
// Same ceiling as a tunnel, for the same reason (one record and one inspector row per spawn). Mirrors
// EditorCell.MaxSuperTunnelSpawns / MaxToolBoxSpawns / MaxPipeSpawns.
export const MAX_SUPER_TUNNEL_SPAWNS = MAX_TUNNEL_SPAWNS;
export const MAX_TOOL_BOX_SPAWNS = MAX_TUNNEL_SPAWNS;
export const MAX_PIPE_SPAWNS = MAX_TUNNEL_SPAWNS;
// What a fresh one holds. Mirrors EditorCell.DefaultSuperTunnelSpawns / DefaultToolBoxSpawns / DefaultPipeSpawns.
export const DEFAULT_SUPER_TUNNEL_SPAWNS = 3;
export const DEFAULT_TOOL_BOX_SPAWNS = 3;
export const DEFAULT_PIPE_SPAWNS = 3;
const DEFAULT_SPAWNS = {
    superTunnel: DEFAULT_SUPER_TUNNEL_SPAWNS,
    toolBox: DEFAULT_TOOL_BOX_SPAWNS,
    pipe: DEFAULT_PIPE_SPAWNS,
};
// Which spawner `c` is, if any.
export function spawnerOf(c) {
    return c.isSuperTunnel ? "superTunnel" : c.isToolBox ? "toolBox" : c.isPipe ? "pipe" : null;
}
// How many boxes a spawner cell holds, as authored (0 when it is not that spawner, or the key is absent).
export function spawnerCount(c, kind) {
    return c[SPAWNER_KEYS[kind].count] ?? 0;
}
// Spawn `index` of a spawner, read the way the compiler reads it (see tunnelSpawnAt).
export function spawnerSpawnAt(cell, kind, index) {
    return tunnelSpawnAt({ ...cell, tunnelSpawns: cell[SPAWNER_KEYS[kind].spawns] ?? [] }, index);
}
export function superTunnelSpawnAt(cell, index) {
    return spawnerSpawnAt(cell, "superTunnel", index);
}
export function toolBoxSpawnAt(cell, index) {
    return spawnerSpawnAt(cell, "toolBox", index);
}
export function pipeSpawnAt(cell, index) {
    return spawnerSpawnAt(cell, "pipe", index);
}
// Converts/adds the cell at (x, y) to a spawner of `kind` (3 boxes unless it already is one). Clears every
// box, tunnel and other-spawner field for withCellAsTunnel's reasons — a stale arrow, paint or queue on a
// wall is a value nothing on screen can clear.
export function withCellAsSpawner(t, x, y, kind) {
    const keys = SPAWNER_KEYS[kind];
    const existing = findCell(t, x, y);
    const base = withCellAdded(t, x, y);
    const already = existing !== undefined && (existing[keys.flag] ?? false);
    const current = already ? spawnerCount(existing, kind) : 0;
    // A spawner already over the ceiling keeps its count and records (see withCellAsTunnel).
    const keep = already && current > MAX_TUNNEL_SPAWNS;
    const count = current > 0 ? clampSpawnCount(current) : DEFAULT_SPAWNS[kind];
    const fallback = existing ? cellTrio(existing) : cellTrio(makeCell(x, y));
    const prev = already ? existing[keys.spawns] ?? undefined : undefined;
    return withCellPatched(base, x, y, {
        isTunnel: false,
        isGate: false,
        isCleared: false,
        isMystery: false,
        isArrow: false,
        isGlide: false,
        isSplit: false,
        splitColorOverrides: null,
        isDouble: false,
        innerColorOverride: MarbleColor.None,
        isJumpingJack: false,
        isDudBox: false,
        colorOverride: MarbleColor.None,
        gateLength: 0,
        gateMultiplier: 1,
        tunnelSpawnCount: 0,
        tunnelSpawns: [],
        ...NO_SPAWNER,
        [keys.flag]: true,
        [keys.count]: keep ? current : count,
        [keys.spawns]: keep ? prev ?? [] : resizeSpawns(prev, count, fallback),
    });
}
export function withCellAsSuperTunnel(t, x, y) {
    return withCellAsSpawner(t, x, y, "superTunnel");
}
export function withCellAsToolBox(t, x, y) {
    return withCellAsSpawner(t, x, y, "toolBox");
}
export function withCellAsPipe(t, x, y) {
    return withCellAsSpawner(t, x, y, "pipe");
}
// --- propeller -------------------------------------------------------------------------------------------
// A standing item tied to the box one step in `propellerDirection` (the game's Propeller): dropping that box
// launches it at a random box elsewhere, which it collects straight to the slices. It can be tied to another
// propeller instead, which sets it off as it takes off. It holds nothing, so there
// is no load to author — only which neighbour it is tied to.
// The order a fresh propeller looks for a box to tie to: below first (the box usually reached first), then
// the sides, then above.
const PROPELLER_TIE_ORDER = [GridDirection.Bottom, GridDirection.Left, GridDirection.Right, GridDirection.Top];
// The cell a propeller at (c.x, c.y) is tied to.
export function propellerTiedCell(c) {
    const d = c.propellerDirection ?? GridDirection.Top;
    const step = d === GridDirection.Top ? [0, -1] : d === GridDirection.Bottom ? [0, 1] : d === GridDirection.Left ? [-1, 0] : [1, 0];
    return { x: c.x + step[0], y: c.y + step[1] };
}
// Converts/adds the cell at (x, y) to a propeller. An existing propeller keeps its direction; a new one ties to
// the first neighbour that holds a box (see PROPELLER_TIE_ORDER), else to the first propeller beside it (a chain:
// that one sets it off as it takes off), or points down when there is neither. Clears
// every box, tunnel and spawner field for withCellAsTunnel's reasons. Same-reference no-op on a propeller.
export function withCellAsPropeller(t, x, y) {
    const existing = findCell(t, x, y);
    let direction = GridDirection.Bottom;
    if (existing && existing.isPropeller) {
        direction = existing.propellerDirection ?? GridDirection.Top;
    }
    else {
        const neighbour = (d) => {
            const n = propellerTiedCell({ ...makeCell(x, y), propellerDirection: d });
            return findCell(t, n.x, n.y);
        };
        const boxed = PROPELLER_TIE_ORDER.find((d) => {
            const at = neighbour(d);
            return at !== undefined && !isWall(at);
        });
        const chained = PROPELLER_TIE_ORDER.find((d) => {
            const at = neighbour(d);
            return at !== undefined && (at.isPropeller ?? false) && !(at.isCleared ?? false);
        });
        if (boxed !== undefined)
            direction = boxed;
        else if (chained !== undefined)
            direction = chained;
    }
    return withCellPatched(withCellAdded(t, x, y), x, y, {
        isTunnel: false,
        isGate: false,
        isCleared: false,
        isMystery: false,
        isArrow: false,
        isGlide: false,
        isSplit: false,
        splitColorOverrides: null,
        isDouble: false,
        innerColorOverride: MarbleColor.None,
        isJumpingJack: false,
        isDudBox: false,
        colorOverride: MarbleColor.None,
        gateLength: 0,
        gateMultiplier: 1,
        tunnelSpawnCount: 0,
        tunnelSpawns: [],
        ...NO_SPAWNER,
        isPropeller: true,
        propellerDirection: direction,
    });
}
// Puts a plain box under the propeller at (x, y), or takes it away (and its paint with it — a propeller with
// nothing under it holds no colour). No-op unless the cell is a propeller.
export function withPropellerBox(t, x, y, on) {
    const cell = findCell(t, x, y);
    if (!cell || !cell.isPropeller) {
        return t;
    }
    return withCellPatched(t, x, y, on ? { propellerHasBox: true } : { propellerHasBox: false, colorOverride: MarbleColor.None });
}
// Whether a propeller cell holds a box under it — a box for every count, paint and badge that asks.
export function holdsHiddenBox(c) {
    return (c.isPropeller ?? false) && (c.propellerHasBox ?? false) && !(c.isCleared ?? false);
}
// Ties the propeller at (x, y) to its neighbour in `direction`. No-op unless the cell is a propeller.
export function withPropellerDirection(t, x, y, direction) {
    const cell = findCell(t, x, y);
    if (!cell || !cell.isPropeller) {
        return t;
    }
    return withCellPatched(t, x, y, { propellerDirection: direction });
}
// The rows a pipe's body runs up through, as the game builds it (MarbleGrid.BuildPipe): every cell above the
// head the template leaves EMPTY, up to the board's top edge or the first authored cell. `top` is the topmost
// row the body covers (the head's own row when the cell above is taken or there is none), `reachesTop` whether
// it ran all the way to row 0 - where the game sends it into the board's top wall.
export function pipeRun(t, x, y) {
    let top = y;
    for (let r = y - 1; r >= 0; r--) {
        if (hasCell(t, x, r)) {
            return { top, reachesTop: false };
        }
        top = r;
    }
    return { top, reachesTop: true };
}
// Sets a spawner's load and resizes its per-spawn records to match (see withTunnelSpawnCount).
export function withSpawnerCount(t, x, y, kind, count) {
    const cell = findCell(t, x, y);
    if (!cell) {
        return t;
    }
    const keys = SPAWNER_KEYS[kind];
    const n = clampSpawnCount(count);
    return withCellPatched(t, x, y, { [keys.count]: n, [keys.spawns]: resizeSpawns(cell[keys.spawns] ?? undefined, n, cellTrio(cell)) });
}
// Patches the index-th spawn of a spawner (see withTunnelSpawn — same ceiling rule).
export function withSpawnerSpawn(t, x, y, kind, index, patch) {
    const cell = findCell(t, x, y);
    if (!cell || spawnerCount(cell, kind) > MAX_TUNNEL_SPAWNS) {
        return t;
    }
    const keys = SPAWNER_KEYS[kind];
    const spawns = resizeSpawns(cell[keys.spawns] ?? undefined, spawnerCount(cell, kind), cellTrio(cell));
    if (index >= 0 && index < spawns.length) {
        spawns[index] = { ...spawns[index], ...patch };
    }
    return withCellPatched(t, x, y, { [keys.spawns]: spawns });
}
export function withSuperTunnelSpawnCount(t, x, y, count) {
    return withSpawnerCount(t, x, y, "superTunnel", count);
}
export function withSuperTunnelSpawn(t, x, y, index, patch) {
    return withSpawnerSpawn(t, x, y, "superTunnel", index, patch);
}
// Sets a horizontal gate of `length` cells (clamped to the board) starting at (x, y) with `multiplier`.
// The head carries the length; continuation cells to the right are (re)created as gate cells; stale
// continuation cells beyond the new span are removed. Cells the gate covers are replaced.
export function withGate(t, x, y, length, multiplier) {
    const len = Math.max(1, Math.min(length, t.columns - x));
    const mult = Math.max(1, multiplier);
    // Drop any cell inside the new span (re-added below) + contiguous stale continuations just past it.
    let items = t.cells.items.filter((c) => !(c.y === y && c.x >= x && c.x < x + len));
    for (let cx = x + len;; cx++) {
        const cont = t.cells.items.find((c) => c.x === cx && c.y === y && c.isGate && c.gateLength === 0);
        if (!cont)
            break;
        items = items.filter((c) => !(c.x === cx && c.y === y));
    }
    const head = { ...makeCell(x, y), isGate: true, gateLength: len, gateMultiplier: mult };
    const added = [head];
    for (let dx = 1; dx < len; dx++) {
        added.push({ ...makeCell(x + dx, y), isGate: true, gateLength: 0, gateMultiplier: 1 });
    }
    return { ...t, cells: { ...t.cells, items: [...items, ...added] } };
}
// The DEFAULT gate shape — what a ⇧5 click places: GATE_LENGTH cells wide, ×GATE_MULTIPLIER, horizontal.
// The author picks the row and where it starts; the span is clamped so a gate started on the last column
// moves left instead of being silently shortened. Length and multiplier are then editable per gate from
// the inspector (the game spans a gate generically and the compiler multiplies by whatever is stored —
// the one-fixed-shape era was an editor rule, not a game one).
export const GATE_LENGTH = 2;
export const GATE_MULTIPLIER = 2;
// The longest a gate headed at (x, y) may grow to: rightward cells that are either covered by THIS gate
// already or empty, capped at the board edge. The cap matters because withGate REPLACES every cell in
// its span — growing a gate must never swallow an authored box beside it without a word, so the
// inspector clamps to this before calling withGate.
export function gateMaxLengthAt(t, x, y) {
    const head = findCell(t, x, y);
    if (!head || !head.isGate || head.gateLength <= 0)
        return 1;
    let max = 0;
    for (let cx = x; cx < t.columns; cx++) {
        const c = findCell(t, cx, y);
        const ownSpan = cx < x + head.gateLength;
        if (c && !ownSpan)
            break; // an authored cell past the current span — the gate stops here
        max++;
    }
    return Math.max(1, max);
}
// True when (x, y) already carries exactly the gate withGate(t, x, y, length, multiplier) would build: same
// head length/multiplier, continuation cells already in place (multiplier 1, as withGate always sets them),
// and no stale continuation trailing past the span for withGate to clean up. withGate itself always rebuilds
// unconditionally (other callers, e.g. duplicateCell, rely on that), so this check is what lets withGateAt
// skip the rebuild — and the undo entry — when a repeated ⇧5 changes nothing.
function hasIdenticalGate(t, x, y, length, multiplier) {
    const head = findCell(t, x, y);
    if (!head || !head.isGate || head.gateLength !== length || head.gateMultiplier !== multiplier) {
        return false;
    }
    for (let dx = 1; dx < length; dx++) {
        const c = findCell(t, x + dx, y);
        if (!c || !c.isGate || c.gateLength !== 0 || c.gateMultiplier !== 1) {
            return false;
        }
    }
    const tail = findCell(t, x + length, y);
    return !(tail && tail.isGate && tail.gateLength === 0); // a stale continuation withGate would still clear
}
// The head of the gate covering (x, y), or undefined when that cell belongs to no gate. A gate is only
// ever addressed through its head — its length lives there — so every op that has to reason about "the
// whole gate under this cell" starts here.
export function gateHeadAt(t, x, y) {
    return t.cells.items.find((c) => c.isGate && c.gateLength > 0 && c.y === y && x >= c.x && x < c.x + c.gateLength);
}
// Where withGateAt would put a gate for a click at (x, y), and whether it can go there at all.
//
// `base` is the template with the gate under the cursor (if any) already removed WHOLE. Replacing a gate
// cell in place is what has to be avoided: withGate only cleans up stale continuations to the RIGHT of the
// new span, so laying a gate one cell right of an existing one left (1, y) as both a continuation of the
// old head AND a head of its own. The validator calls that "Multiple gates found" — a Warning, so the
// broken board saved, compiled and published.
//
// Everything the clamped span still covers after that has to be free: the author picked a spot, and a gate
// two cells wide would otherwise swallow whatever authored cell sits beside it without a word.
function gatePlacement(t, x, y) {
    // A click anywhere on a gate addresses THAT gate, so the span is measured from its HEAD — the rule every
    // other gate op in this file follows via gateHeadAt. Measuring from the clamped CLICK instead meant a
    // press on a continuation cell proposed a span one cell to the right of the gate already there: the
    // "nothing to do" test missed, the gate was torn down and rebuilt one cell over, and the rebuild took
    // any lock drawn over its old span with it. It also made gateFitsAt answer for the wrong span, so the
    // page announced "Gate needs 2 free cells" about a gate plainly on the board.
    const head = gateHeadAt(t, x, y);
    const gx = Math.max(0, Math.min(head ? head.x : x, t.columns - GATE_LENGTH));
    const base = head ? withGateClearedAt(t, x, y) : t;
    let fits = t.columns >= GATE_LENGTH;
    for (let dx = 0; fits && dx < GATE_LENGTH; dx++) {
        fits = !hasCell(base, gx + dx, y);
    }
    return { base, gx, fits };
}
// Whether a gate can be placed for a click at (x, y). withGateAt returns the same template both when it
// refuses and when the gate is already exactly there, and those two need different words in the UI — this
// is how the page tells "there is no room" (worth saying) from "nothing to do" (a repeated ⇧5, silent).
export function gateFitsAt(t, x, y) {
    return gatePlacement(t, x, y).fits;
}
export function withGateAt(t, x, y) {
    if (t.columns < GATE_LENGTH) {
        return t; // no room for a gate on a board this narrow
    }
    // One placement, read once: the "nothing to do" test has to ask about the SAME span the rebuild would
    // lay down, or a press that changes nothing looks like a press that has to tear something up.
    const placement = gatePlacement(t, x, y);
    // Before anything is torn down: an identical gate already here means there is nothing to do, and
    // saying so with the SAME reference is what keeps a repeated ⇧5 off the undo stack.
    if (hasIdenticalGate(t, placement.gx, y, GATE_LENGTH, GATE_MULTIPLIER)) {
        return t;
    }
    if (!placement.fits) {
        return t;
    }
    return withGate(placement.base, placement.gx, y, GATE_LENGTH, GATE_MULTIPLIER);
}
// Removes the entire gate covering (x, y) — the head plus all its continuation cells (so right-clicking
// any gate cell deletes the whole gate, not just one cell) — along with every modifier left pointing at a
// cell that removal emptied. Falls back to the single cell if no head found.
//
// The modifier sweep is the same rule the non-gate delete path already follows (withModifiersClearedAt on
// top of withCellRemoved); without it, deleting a gate someone had drawn a link to left that link pointing
// at an empty cell, which the validator only WARNS about — so it saved and published. A modifier over a
// cell the caller is about to refill (withGateAt replacing a gate) goes too: one rule for the whole span
// is predictable and undoable, and a link drawn onto a gate belongs to that gate.
export function withGateClearedAt(t, x, y) {
    const head = gateHeadAt(t, x, y);
    if (!head) {
        return withCellRemoved(t, x, y);
    }
    const xs = [];
    for (let dx = 0; dx < head.gateLength; dx++) {
        xs.push(head.x + dx);
    }
    return {
        ...t,
        cells: {
            items: t.cells.items.filter((c) => !(c.y === y && c.isGate && xs.includes(c.x))),
            modifiers: t.cells.modifiers.filter((m) => !xs.some((cx) => modifierCovers(m, cx, y))),
        },
    };
}
// Whether (x, y) can carry a connector. The game's own rule, from CellSetGenerator.IsConnectable: "Connectors
// only link two boxes, so tunnels and gate cells (both walls, not boxes) are excluded." The hand editor never
// applied it — it offered Connect on any occupied cell — so both the menu and the drag read it from here now,
// and validateTemplate says the same thing about a file that already holds such a link.
export function isConnectable(t, x, y) {
    const cell = findCell(t, x, y);
    return cell !== undefined && !isWall(cell);
}
// --- drag-to-move -------------------------------------------------------------------------------------
// The cells that move together when the item at (x, y) is dragged: a whole gate (its head + every
// continuation cell), BOTH cells of a Connected pair, or just the single box/tunnel. Empty when the cell
// has no item.
export function moveGroupCells(t, x, y) {
    const cell = findCell(t, x, y);
    if (!cell) {
        return [];
    }
    if (cell.isGate) {
        const head = gateHeadAt(t, x, y);
        if (head) {
            return Array.from({ length: head.gateLength }, (_, i) => ({ x: head.x + i, y: head.y }));
        }
        return [{ x, y }];
    }
    // `cells` is read straight off Drive with no schema check, so a hand-edited modifier can be missing it
    // entirely — and this runs on every step of a drag, where a throw would kill the gesture mid-move.
    const conn = t.cells.modifiers.find((m) => m.type === CellModifierType.Connected && Array.isArray(m.cells) && m.cells.some((p) => p.x === x && p.y === y));
    if (conn) {
        // Only endpoints that can actually carry a connector travel, i.e. boxes that are still there:
        //   * a link whose other end was DELETED would otherwise drag a phantom cell along, and previewMove
        //     judges the drop on every group cell's destination — so an ordinary one-cell move could be
        //     refused because of something that isn't on the board;
        //   * an end on a WALL is worse than a phantom. A gate is one item spread over gateLength cells,
        //     addressed through its head, so carrying a single one of them away left the head still claiming
        //     its full span and a stray continuation (gateLength 0, which GridCanvas draws nothing for)
        //     sitting invisibly on some other cell. Only a Warning, so that board saved and published.
        // The grabbed cell itself always travels — it may be a tunnel, and a tunnel has to stay draggable.
        return conn.cells.filter((p) => (p.x === x && p.y === y) || isConnectable(t, p.x, p.y)).map((p) => ({ x: p.x, y: p.y }));
    }
    return [{ x, y }];
}
// Whether the group grabbed at `from` can drop at `to` — every group cell, translated by the same delta,
// lands in-bounds on a free cell (its own vacated cells don't count as occupied). Also returns those
// destination cells for previewing. A zero delta is not a valid move.
export function previewMove(t, from, to) {
    const group = moveGroupCells(t, from.x, from.y);
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const src = new Set(group.map((p) => `${p.x},${p.y}`));
    const cells = group.map((p) => ({ x: p.x + dx, y: p.y + dy }));
    const valid = group.length > 0 &&
        (dx !== 0 || dy !== 0) &&
        cells.every((d) => d.x >= 0 && d.y >= 0 && d.x < t.columns && d.y < t.rows && (!findCell(t, d.x, d.y) || src.has(`${d.x},${d.y}`)));
    return { cells, valid };
}
// Moves the group grabbed at `from` to `to` (translating every group cell by the same delta), keeping
// modifiers pointing at the moved cells. Returns the new template, or null if the move is invalid
// (out of bounds, or any destination cell is occupied by something outside the group).
export function moveCell(t, from, to) {
    if (!previewMove(t, from, to).valid) {
        return null;
    }
    const group = moveGroupCells(t, from.x, from.y);
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const src = new Set(group.map((p) => `${p.x},${p.y}`));
    return {
        ...t,
        cells: {
            items: t.cells.items.map((c) => (src.has(`${c.x},${c.y}`) ? { ...c, x: c.x + dx, y: c.y + dy } : c)),
            modifiers: t.cells.modifiers.map((m) => modifierAfterMove(m, src, dx, dy)),
        },
    };
}
// Where a modifier ends up when the item cells in `moved` (keys "x,y") shift by (dx, dy) and the modifier
// itself is NOT carried whole (selectionOps carries a modifier whole when every cell of it moves). Shared by
// the one-item drag and the selection move, so both follow one rule.
//
// Lock panels do NOT travel with what stands on them: their two entries are the CORNERS of an area of the
// board, not cells that belong to an item. Translating whichever corner happened to share coordinates with a
// dragged cell turned the one 3×3 square the game ships into a 2×3 rectangle — and nothing validates a lock's
// size, so it saved and published that way.
//
// `cells` arrives off Drive with no schema check, so a hand-edited modifier can be missing it entirely —
// reachable on every drag step. Left untranslated (not fabricated) when it can't be read: there's nothing
// sensible to move, and a throw here would kill the gesture mid-move.
//
// A sliding door and a laser gate's RUN stay put for the lock's reason: their two ends span a stretch of
// board, and moving one end with a box reshaped the run (or bent it off its row). The laser gate's KEY is
// different — it is one box, wherever that box goes — so index 2 travels. The metal box and the bolt name an
// area / a run of board the same way, so they stay put too.
export function modifierAfterMove(m, moved, dx, dy) {
    if (m.type === CellModifierType.LockPanel || !Array.isArray(m.cells)) {
        return m;
    }
    if (m.type === CellModifierType.MetalBox || m.type === CellModifierType.Bolt || m.type === CellModifierType.Elevator || m.type === CellModifierType.GlassPanel) {
        return m;
    }
    const run = m.type === CellModifierType.SlidingDoor || m.type === CellModifierType.LaserGate;
    return {
        ...m,
        cells: m.cells.map((p, i) => (!run || (m.type === CellModifierType.LaserGate && i === 2)) && moved.has(`${p.x},${p.y}`) ? { x: p.x + dx, y: p.y + dy } : p),
    };
}
// --- duplicate ----------------------------------------------------------------------------------------
// A verbatim copy of `c` at (x, y): every authored field, with the per-spawn records deep-copied so the
// two cells never share one array.
function cloneCellAt(c, x, y) {
    return {
        ...c,
        x,
        y,
        tunnelSpawns: c.tunnelSpawns ? c.tunnelSpawns.map((s) => ({ ...s })) : [],
        superTunnelSpawns: c.superTunnelSpawns ? c.superTunnelSpawns.map((s) => ({ ...s })) : [],
        toolBoxSpawns: c.toolBoxSpawns ? c.toolBoxSpawns.map((s) => ({ ...s })) : [],
    };
}
// The first free cell in reading order (row by row, left to right) — the "nearest free top-left corner".
function firstFreeCell(t) {
    for (let y = 0; y < t.rows; y++) {
        for (let x = 0; x < t.columns; x++) {
            if (!hasCell(t, x, y)) {
                return { x, y };
            }
        }
    }
    return null;
}
// The first run of `len` consecutive free cells on one row, in reading order (a gate needs its whole span).
function firstFreeRun(t, len) {
    for (let y = 0; y < t.rows; y++) {
        for (let x = 0; x + len <= t.columns; x++) {
            let free = true;
            for (let dx = 0; dx < len; dx++) {
                if (hasCell(t, x + dx, y)) {
                    free = false;
                    break;
                }
            }
            if (free) {
                return { x, y };
            }
        }
    }
    return null;
}
// Duplicates the item at (x, y) `count` times into the first free cells, scanning in reading order. The
// copy is the SAME item somewhere else — every authored field rides along; modifiers do not (a lock or a
// link belongs to the cells it was drawn over). Duplicating any cell of a gate duplicates the whole gate.
// Returns the new template plus how many copies actually fit, so the caller can say "3 of 5 — board full".
export function duplicateCell(t, x, y, count) {
    const src = findCell(t, x, y);
    if (!src || count <= 0) {
        return { template: t, placed: 0 };
    }
    const head = src.isGate ? gateHeadAt(t, x, y) : undefined;
    let out = t;
    let placed = 0;
    for (let i = 0; i < count; i++) {
        if (head) {
            const spot = firstFreeRun(out, head.gateLength);
            if (!spot) {
                break;
            }
            // withGate builds its head from makeCell, i.e. with the trio back at 0 — so the copy needs the
            // authored values put back on it, or "every authored field rides along" would hold for a box and
            // quietly not for a gate. Read off the HEAD, since that is where a gate is authored.
            out = withCellPatched(withGate(out, spot.x, spot.y, head.gateLength, head.gateMultiplier), spot.x, spot.y, {
                group: head.group,
                sibling: head.sibling,
                sortOrder: head.sortOrder,
            });
        }
        else {
            const spot = firstFreeCell(out);
            if (!spot) {
                break;
            }
            out = { ...out, cells: { ...out.cells, items: [...out.cells.items, cloneCellAt(src, spot.x, spot.y)] } };
        }
        placed++;
    }
    return { template: out, placed };
}
// Adds a "connected" modifier linking two cells.
export function withConnected(t, ax, ay, bx, by) {
    const mod = { type: CellModifierType.Connected, cells: [{ x: ax, y: ay }, { x: bx, y: by }], counter: 0 };
    return { ...t, cells: { ...t.cells, modifiers: [...t.cells.modifiers, mod] } };
}
// Whether a modifier applies to (x, y): a listed endpoint, or — for a lock panel — anywhere inside the
// rectangle its two corners span.
export function modifierCovers(m, x, y) {
    // `cells` arrives off Drive with no schema check, so it can be missing entirely — this runs on every
    // render (lockIndexAt, from renderInspector) and on every cell removal (withModifiersClearedAt), so a
    // throw here blanks the editor rather than tolerating one bad key. Treated as covering nothing.
    if (!Array.isArray(m.cells)) {
        return false;
    }
    const inRect = (a, b) => x >= Math.min(a.x, b.x) && x <= Math.max(a.x, b.x) && y >= Math.min(a.y, b.y) && y <= Math.max(a.y, b.y);
    if ((m.type === CellModifierType.LockPanel || m.type === CellModifierType.Elevator) && m.cells.length >= 2) {
        return inRect(m.cells[0], m.cells[1]);
    }
    if ((m.type === CellModifierType.MetalBox || m.type === CellModifierType.GlassPanel) && m.cells.length >= 1) {
        const a = m.cells[0];
        return inRect(a, m.cells[1] ?? { x: a.x + 1, y: a.y + 1 });
    }
    // A run covers every cell between its two ends — deleting a box half way along a door takes the door with
    // it, as deleting a box under a lock takes the lock. A laser gate also covers its KEY (cells[2]), which
    // stands apart from the run: the rectangle is never stretched out to reach it (the game's AreaCellCount).
    if ((m.type === CellModifierType.SlidingDoor || m.type === CellModifierType.LaserGate || m.type === CellModifierType.Bolt) && m.cells.length >= 2) {
        const a = m.cells[0];
        const b = m.cells[1];
        const straight = a.x === b.x || a.y === b.y;
        return (straight && inRect(a, b)) || m.cells.some((p) => p.x === x && p.y === y);
    }
    return m.cells.some((p) => p.x === x && p.y === y);
}
// The index of the lock panel covering (x, y), or -1. The inspector uses this to show the lock's own
// fields next to the cell's.
export function lockIndexAt(t, x, y) {
    return t.cells.modifiers.findIndex((m) => m.type === CellModifierType.LockPanel && modifierCovers(m, x, y));
}
// Adds a LOCK_SIZE × LOCK_SIZE lock panel whose TOP-LEFT corner is (x, y), clamped so the square always
// fits the board (a lock started on the last column moves left instead of hanging off the edge). Any lock
// it overlaps is REPLACED — two stacked panels over one cell would be ambiguous to the author and to the
// game. Connected modifiers are untouched.
//
// `counter` is the value a NEW lock starts at. A lock already sitting on exactly these clamped corners is
// left alone — same reference back, no undo entry — counter and all: the author tunes that number in the
// inspector (withLockCounter), so re-placing the same square used to quietly reset a lock set to 10 back
// to the default.
export function withLockPanel(t, x, y, counter) {
    const ax = Math.max(0, Math.min(x, t.columns - LOCK_SIZE));
    const ay = Math.max(0, Math.min(y, t.rows - LOCK_SIZE));
    const bx = Math.min(t.columns - 1, ax + LOCK_SIZE - 1);
    const by = Math.min(t.rows - 1, ay + LOCK_SIZE - 1);
    const c = Math.max(0, counter);
    // `cells` arrives off Drive with no schema check, so a hand-edited modifier can be missing it entirely
    // — reachable every time a lock is placed (⇧6), since both checks below scan every existing modifier.
    // Neither identical to nor overlapping the new lock when it can't be read; withLockPanel simply adds
    // the new one alongside it instead of throwing.
    const isIdentical = (m) => m.type === CellModifierType.LockPanel &&
        Array.isArray(m.cells) &&
        m.cells.length === 2 &&
        m.cells[0].x === ax && m.cells[0].y === ay &&
        m.cells[1].x === bx && m.cells[1].y === by;
    if (t.cells.modifiers.some(isIdentical)) {
        return t;
    }
    const overlaps = (m) => {
        if (m.type !== CellModifierType.LockPanel || !Array.isArray(m.cells) || m.cells.length < 2) {
            return false;
        }
        const [p, q] = m.cells;
        return Math.min(p.x, q.x) <= bx && Math.max(p.x, q.x) >= ax && Math.min(p.y, q.y) <= by && Math.max(p.y, q.y) >= ay;
    };
    const mod = {
        type: CellModifierType.LockPanel,
        cells: [{ x: ax, y: ay }, { x: bx, y: by }],
        counter: c,
    };
    return { ...t, cells: { ...t.cells, modifiers: [...t.cells.modifiers.filter((m) => !overlaps(m)), mod] } };
}
// Sets the counter of the index-th modifier (how many customers must be cleared before a lock opens).
// Returns the SAME template when nothing changes, so setTemplate's no-op guard skips the undo entry.
export function withLockCounter(t, index, counter) {
    const mods = t.cells.modifiers;
    const c = Math.max(0, counter);
    if (index < 0 || index >= mods.length || mods[index].counter === c) {
        return t;
    }
    return { ...t, cells: { ...t.cells, modifiers: mods.map((m, i) => (i === index ? { ...m, counter: c } : m)) } };
}
// Removes any modifier that touches (x, y): a listed endpoint, or (for a lock panel) its covered rectangle.
export function withModifiersClearedAt(t, x, y) {
    return { ...t, cells: { ...t.cells, modifiers: t.cells.modifiers.filter((m) => !modifierCovers(m, x, y)) } };
}
// Patches the generation params. Same-reference no-op when every field named in `patch` already holds that
// value — these fields are driven by number boxes that fire on every keystroke, and without this a value
// re-entered unchanged burned an entry off the 50-deep undo stack. The two array fields compare by identity,
// so a freshly built list always counts as a change; that errs towards keeping history, not losing it.
export function withGeneration(t, patch) {
    const keys = Object.keys(patch);
    if (keys.every((k) => t.generation[k] === patch[k])) {
        return t;
    }
    return { ...t, generation: { ...t.generation, ...patch } };
}
// Sets the level's name. Returns the SAME template when the name is unchanged (React fires `change` for
// things that leave the text alone — an IME commit, a paste of the identical string), so setMetadata's
// no-op guard skips both the undo entry and the draft write.
export function withName(t, name) {
    return t.name === name ? t : { ...t, name };
}
// Sets the authoring difficulty (metadata; the compiler ignores it). Same-reference no-op when unchanged,
// like withName — a <select> fires `change` for a re-pick of the value already showing.
export function withDifficulty(t, difficulty) {
    return t.difficulty === difficulty ? t : { ...t, difficulty };
}
// Whether stepping from `a` to `b` leaves an already-compiled preview still describing the board. Only
// `difficulty` is metadata the compiler does not read, so only a difficulty-only change may keep it.
//
// KEEP IN SYNC WITH EditorLevel: this lists every field EXCEPT `difficulty`, and a new field that is not
// listed silently joins the "only difficulty differs" side — the preview would then survive a change that
// invalidated it. `sliceColorOverrides` is the reason this moved out of the page and grew a test: painting a
// slice changes NOTHING else (`cells` keeps its identity), so an unlisted field made every painted slice
// look like a difficulty change — preview kept, and Play sending a level the board no longer describes.
export function onlyDifficultyDiffers(a, b) {
    return (a.cells === b.cells &&
        a.generation === b.generation &&
        a.columns === b.columns &&
        a.rows === b.rows &&
        a.name === b.name &&
        a.schemaVersion === b.schemaVersion &&
        a.sliceColorOverrides === b.sliceColorOverrides &&
        a.frozenSlices === b.frozenSlices);
}
// Board summary for the header/inspector.
export function templateStats(t) {
    const items = t.cells.items.filter((c) => c != null); // a hand-edited file can hold a null entry (see findCell)
    const boxes = items.filter((c) => !isWall(c) || holdsHiddenBox(c)).length;
    // Distinct groups across boxes (their own group) and tunnels (each spawn's group).
    const groupSet = new Set();
    for (const c of items) {
        if (c.isGate || c.isCleared || (c.isPropeller && !holdsHiddenBox(c)))
            continue;
        if (c.isTunnel && c.tunnelSpawns && c.tunnelSpawns.length > 0) {
            for (const s of c.tunnelSpawns)
                groupSet.add(s.group);
        }
        else if (c.isSuperTunnel && c.superTunnelSpawns && c.superTunnelSpawns.length > 0) {
            for (const s of c.superTunnelSpawns)
                groupSet.add(s.group);
        }
        else if (c.isToolBox && c.toolBoxSpawns && c.toolBoxSpawns.length > 0) {
            for (const s of c.toolBoxSpawns)
                groupSet.add(s.group);
        }
        else {
            groupSet.add(c.group);
        }
    }
    const groups = groupSet.size;
    return {
        boxes,
        mystery: items.filter((c) => c.isMystery).length,
        tunnels: items.filter((c) => c.isTunnel || c.isSuperTunnel).length,
        gates: items.filter((c) => c.isGate).length,
        cleared: items.filter((c) => c.isCleared).length,
        groups,
    };
}
